import { FunctionRegistry, flattenArgs, toNum, toStr } from './index';
import { FormulaValue, FormulaError, FormulaErrorType } from '../types';
import { smartRound } from '../evaluator';

export function registerInformationFunctions(registry: FunctionRegistry): void {
    registry.register('ISBLANK', (...args: FormulaValue[]) => {
        const v = args[0];
        return v === null || v === undefined || v === '';
    });

    // ISERR: true if error but NOT #N/A.
    // Since errors are thrown in this engine and not passed as values,
    // we can only catch FormulaError instances that were explicitly passed.
    registry.register('ISERR', (...args: FormulaValue[]) => {
        const v = args[0];
        if (v instanceof FormulaError) {
            return v.type !== FormulaErrorType.NA;
        }
        return false;
    });

    // ISERROR: true if any error.
    registry.register('ISERROR', (...args: FormulaValue[]) => {
        const v = args[0];
        return v instanceof FormulaError;
    });

    registry.register('ISEVEN', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        if (!isFinite(v)) throw new FormulaError(FormulaErrorType.NUM, 'ISEVEN requires a finite number');
        return Math.trunc(v) % 2 === 0;
    });

    // ISFORMULA: always false — no formula cells exist in this context.
    registry.register('ISFORMULA', () => false);

    registry.register('ISLOGICAL', (...args: FormulaValue[]) => {
        return typeof args[0] === 'boolean';
    });

    // ISNA: true if value is a #N/A error.
    registry.register('ISNA', (...args: FormulaValue[]) => {
        const v = args[0];
        return v instanceof FormulaError && v.type === FormulaErrorType.NA;
    });

    registry.register('ISNONTEXT', (...args: FormulaValue[]) => {
        return typeof args[0] !== 'string';
    });

    registry.register('ISNUMBER', (...args: FormulaValue[]) => {
        return typeof args[0] === 'number' && isFinite(args[0]);
    });

    registry.register('ISODD', (...args: FormulaValue[]) => {
        const v = toNum(args[0]);
        if (!isFinite(v)) throw new FormulaError(FormulaErrorType.NUM, 'ISODD requires a finite number');
        return Math.abs(Math.trunc(v)) % 2 !== 0;
    });

    // ISREF: always false — no cell references in this context.
    registry.register('ISREF', () => false);

    registry.register('ISTEXT', (...args: FormulaValue[]) => {
        return typeof args[0] === 'string';
    });

    // NA: throw #N/A error.
    registry.register('NA', () => {
        throw new FormulaError(FormulaErrorType.NA, '#N/A');
    });

    // N: convert to number (numbers stay, TRUE=1, FALSE=0, everything else 0).
    registry.register('N', (...args: FormulaValue[]) => {
        const v = args[0];
        if (typeof v === 'number') return isFinite(v) ? v : 0;
        if (typeof v === 'boolean') return v ? 1 : 0;
        return 0;
    });

    // SHEET / SHEETS: always return 1 — single sheet context.
    registry.register('SHEET', () => 1);
    registry.register('SHEETS', () => 1);
}
